/**
 * Employee Filtering Utilities
 * Provides robust filtering functions for employee data
 */

/**
 * Filter employees by search term (name or email)
 * @param {Array} employees - Array of employee objects
 * @param {string} searchTerm - Search string
 * @returns {Array} Filtered employees
 */
export const filterBySearch = (employees, searchTerm) => {
  if (!searchTerm || searchTerm.trim() === "") return employees;
  
  const lowerSearch = searchTerm.toLowerCase().trim();
  return employees.filter((employee) => {
    const name = employee.name?.toLowerCase() || "";
    const email = employee.email?.toLowerCase() || "";
    return name.includes(lowerSearch) || email.includes(lowerSearch);
  });
};

/**
 * Filter employees by job role ID
 * @param {Array} employees - Array of employee objects
 * @param {Array} apiEmployees - Original API employee data with primaryJobRoleId
 * @param {string} roleId - Job role ID to filter by
 * @returns {Array} Filtered employees
 */
export const filterByJobRole = (employees, apiEmployees, roleId) => {
  if (!roleId) return employees;
  
  return employees.filter((employee) => {
    const apiEmployee = apiEmployees.find((e) => e.id === employee.id);
    return apiEmployee?.primaryJobRoleId === roleId;
  });
};

/**
 * Filter employees by department
 * @param {Array} employees - Array of employee objects
 * @param {string} department - Department name to filter by
 * @returns {Array} Filtered employees
 */
export const filterByDepartment = (employees, department) => {
  if (!department || department.trim() === "") return employees;
  
  const lowerDepartment = department.toLowerCase().trim();
  return employees.filter((employee) => {
    const empDepartment = employee.department?.toLowerCase() || "";
    return empDepartment === lowerDepartment;
  });
};

/**
 * Filter employees by employment type
 * @param {Array} employees - Array of employee objects
 * @param {string} employmentType - Employment type ('Full-time', 'Part-time', 'Casual')
 * @returns {Array} Filtered employees
 */
export const filterByEmploymentType = (employees, employmentType) => {
  if (!employmentType) return employees;
  
  return employees.filter((employee) => {
    return employee.employmentType === employmentType;
  });
};

/**
 * Filter employees by TRONC eligibility status
 * @param {Array} employees - Array of employee objects
 * @param {string} eligibilityStatus - Eligibility status ('Eligible', 'Not Eligible')
 * @returns {Array} Filtered employees
 */
export const filterByEligibility = (employees, eligibilityStatus) => {
  if (!eligibilityStatus) return employees;
  
  return employees.filter((employee) => {
    return employee.eligibilityStatus === eligibilityStatus;
  });
};

/**
 * Filter employees by location
 * @param {Array} employees - Array of employee objects
 * @param {Array} apiEmployees - Original API employee data with locationId
 * @param {string} locationId - Location ID to filter by
 * @returns {Array} Filtered employees
 */
export const filterByLocation = (employees, apiEmployees, locationId) => {
  if (!locationId) return employees;
  
  return employees.filter((employee) => {
const apiEmployee = apiEmployees.find((e) => e.id === employee.id);
  return apiEmployee?.locationId === locationId;
  });
};

/**
 * Apply all filters to employee list
 * @param {Array} employees - Array of employee objects
 * @param {Array} apiEmployees - Original API employee data
 * @param {Object} filters - Filter criteria
 * @param {string} filters.searchTerm - Search term for name/email
 * @param {string} filters.selectedRole - Job role ID
 * @param {string} filters.selectedDepartment - Department name
 * @param {string} filters.selectedEmploymentType - Employment type
 * @param {string} filters.selectedEligibilityStatus - Eligibility status
 * @param {string} filters.selectedLocation - Location ID
 * @returns {Array} Filtered employees
 */
export const applyAllFilters = (employees, apiEmployees, filters) => {
  let filtered = employees;

  // Apply search filter
  if (filters.searchTerm) {
    filtered = filterBySearch(filtered, filters.searchTerm);
  }

  // Apply job role filter
  if (filters.selectedRole) {
    filtered = filterByJobRole(filtered, apiEmployees, filters.selectedRole);
  }

  // Apply department filter
  if (filters.selectedDepartment) {
    filtered = filterByDepartment(filtered, filters.selectedDepartment);
  }

  // Apply employment type filter
  if (filters.selectedEmploymentType) {
    filtered = filterByEmploymentType(filtered, filters.selectedEmploymentType);
  }

  // Apply eligibility filter
  if (filters.selectedEligibilityStatus) {
    filtered = filterByEligibility(filtered, filters.selectedEligibilityStatus);
  }

  // Apply location filter
  if (filters.selectedLocation) {
    filtered = filterByLocation(filtered, apiEmployees, filters.selectedLocation);
  }

  return filtered;
};

/**
 * Calculate employment type from hours worked
 * @param {number} hoursPerWeek - Contracted hours per week
 * @returns {string} Employment type
 */
export const calculateEmploymentType = (hoursPerWeek) => {
  if (!hoursPerWeek || hoursPerWeek === 0) return "Casual";
  if (hoursPerWeek >= 35) return "Full-time";
  if (hoursPerWeek >= 20) return "Part-time";
  return "Casual";
};

/**
 * Get count of active filters
 * @param {Object} filters - Filter criteria
 * @returns {number} Count of active filters
 */
export const getActiveFilterCount = (filters) => {
  let count = 0;
  
  if (filters.searchTerm && filters.searchTerm.trim()) count++;
  if (filters.selectedRole) count++;
  if (filters.selectedDepartment) count++;
  if (filters.selectedEmploymentType) count++;
  if (filters.selectedEligibilityStatus) count++;
  if (filters.selectedLocation) count++;
  
  return count;
};

/**
 * Get human-readable labels for active filters
 * @param {Object} filters - Filter criteria
 * @param {Array} jobRoles - Job roles array for label lookup
 * @param {Array} locations - Locations array for label lookup
 * @returns {Array} Array of filter labels
 */
export const getActiveFilterLabels = (filters, jobRoles = [], locations = []) => {
  const labels = [];
  
  if (filters.searchTerm && filters.searchTerm.trim()) {
    labels.push(`Search: "${filters.searchTerm}"`);
  }
  
  if (filters.selectedRole) {
    const role = jobRoles.find(r => r.id === filters.selectedRole);
    labels.push(`Role: ${role?.name || filters.selectedRole}`);
  }
  
  if (filters.selectedDepartment) {
    labels.push(`Department: ${filters.selectedDepartment}`);
  }
  
  if (filters.selectedEmploymentType) {
    labels.push(`Type: ${filters.selectedEmploymentType}`);
  }
  
  if (filters.selectedEligibilityStatus) {
    labels.push(`Status: ${filters.selectedEligibilityStatus}`);
  }
  
  if (filters.selectedLocation) {
    const location = locations.find(l => l.id === filters.selectedLocation);
    labels.push(`Location: ${location?.name || filters.selectedLocation}`);
  }
  
  return labels;
};

export default {
  filterBySearch,
  filterByJobRole,
  filterByDepartment,
  filterByEmploymentType,
  filterByEligibility,
  filterByLocation,
  applyAllFilters,
  calculateEmploymentType,
  getActiveFilterCount,
  getActiveFilterLabels,
};
